import React, { useMemo, useState } from 'react';
import DefaultLayout from '@/layout/DefaultLayout';
import Breadcrumb from '@/components/Breadcrumb';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import DynamicTable, { SelectColumnFilter } from '@/components/DynamicTable';
import Pagination from '@/components/Pagination';
import Spinner from '@/components/Spinner';
import { DeleteButton, EditButton } from '@/components/shared/Button';
import DeleteConfirmationModal from '@/components/shared/DeleteConfirmationModal';
import {
  CompanySubcontractorsResponse,
  deleteCompanySubcontractor,
  getCompanySubcontractors,
} from '@/services/CompanySubcontractorService';
import { useTranslation } from 'react-i18next';
import { TaskStatus } from '@/enums/TaskStatus';
import { stateColors } from '@/enums/RequestLeaveState';
import moment from 'moment';
import { Currency } from '@/enums/Currency';
const userTimeZone = moment.tz.guess();

function CompanySubcontractors() {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState<string>('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRow, setSelectedRow] = useState('');
  const navigate = useNavigate();

  const { t } = useTranslation();

  const ActionButtonsCell = ({ value }: any) => {
    return (
      <div>
        <DeleteButton
          onClick={() => {
            setSelectedRow(value);
            setIsModalVisible(true);
          }}
        >
          {t('CompanySubcontractors.deleteButton')}
        </DeleteButton>

        <EditButton
          type="button"
          onClick={() => {
            navigate(`${value}`);
          }}
        >
          {t('CompanySubcontractors.editButton')}
        </EditButton>
      </div>
    );
  };

  const handleCancelDelete = () => {
    setIsModalVisible(false);
  };

  const handleDeleteConfirmation = () => {
    handleDeleteSubcontractor.mutate(selectedRow);
    setIsModalVisible(false);
  };

  const columns = useMemo(
    () => [
      {
        Header: t('CompanySubcontractors.actionsColumn'),
        accessor: 'id',
        Cell: ({ cell: { value } }: any) => <ActionButtonsCell value={value} />,
      },
      {
        Header: t('CompanySubcontractors.nameColumn'),
        accessor: 'name',
        Filter: SelectColumnFilter,
      },
      {
        Header: t('CompanySubcontractors.contactColumn'),
        accessor: 'contact',
      },
      {
        Header: t('CompanySubcontractors.amountHTColumn'),
        accessor: 'amountHT',
        Cell: ({ row, cell: { value } }) => {
          const currencyTxt = Currency[row.original['currency']];
          return <div>{value + ' ' + currencyTxt}</div>;
        },
      },
      {
        Header: t('CompanySubcontractors.amountTTCColumn'),
        accessor: 'amountTTC',
        Cell: ({ row, cell: { value } }) => {
          const currencyTxt = Currency[row.original['currency']];
          return <div>{value + ' ' + currencyTxt}</div>;
        },
      },
      {
        Header: t('CompanySubcontractors.startDateColumn'),
        accessor: 'startDate',
        Cell: ({ cell: { value } }) => {
          const formattedValue = moment
            .utc(value)
            .tz(userTimeZone)
            .format('YYYY-MM-DD hh:mm'); // Customize the format as you need
          return (
            <input
              type="text"
              className="min-w-40 dark:bg-boxdark-2 dark:text-bodydark"
              value={formattedValue}
              readOnly
            />
          );
        },
      },
      {
        Header: t('CompanySubcontractors.statusColumn'),
        accessor: 'status',
        Cell: ({ cell: { value } }) => {
          const frontendState = TaskStatus[value];
          const bgColor = stateColors[frontendState] || 'bg-gray-300';

          return (
            <div
              className={`min-w-50 w-40 rounded-md text-center ${bgColor} px-2.5  py-2.5 text-sm text-white`}
            >
              {frontendState !== null ? t(`TaskStatus.${frontendState}`) : ''}
            </div>
          );
        },
      },
    ],
    [t]
  );

  const queryClient = useQueryClient();

  const {
    isLoading,
    isError,
    data: subcontractorsData,
  } = useQuery<CompanySubcontractorsResponse | undefined>(
    ['CompanySubcontractors', pageIndex, pageSize, search],
    () =>
      getCompanySubcontractors({
        fromvalue: pageIndex,
        takevalue: pageSize,
        search: search,
      }),
    {
      refetchOnWindowFocus: false,
      staleTime: 6000,
      keepPreviousData: true,
    }
  );

  const handleDeleteSubcontractor = useMutation(
    async (id: string) => {
      await deleteCompanySubcontractor(id);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          'CompanySubcontractors',
          pageIndex,
          pageSize,
          search,
        ]);
      },
    }
  );

  const pageCount = useMemo(() => {
    return Math.ceil((subcontractorsData?.count ?? 0) / pageSize);
  }, [pageSize, subcontractorsData]);

  const nextPage = () => setPageIndex(pageIndex + 1);
  const previousPage = () => setPageIndex(pageIndex - 1);
  const onPageChange = (newPageIndex: number) => setPageIndex(newPageIndex);
  const onPageSizeChange = (newPageSize: number) => {
    setPageIndex(0);
    setPageSize(newPageSize);
  };

  return (
    <DefaultLayout>
      <Breadcrumb
        pageName={t('CompanySubcontractors.pageName')}
        addbtn={true}
        addRouter="new"
      />
      {isLoading ? (
        <div className="flex items-center justify-center">
          <Spinner height="3rem" width="3rem" />
        </div>
      ) : (
        <div className="flex flex-col">
          <DynamicTable
            dataCount={subcontractorsData?.count}
            data={subcontractorsData?.companySubcontractors ?? []}
            columns={columns}
            setSearch={setSearch}
            exportPdf={false}
          />
          {isModalVisible && (
            <DeleteConfirmationModal
              isOpen={isModalVisible}
              onDelete={handleDeleteConfirmation}
              onCancel={handleCancelDelete}
            />
          )}
          <Pagination
            pageIndex={pageCount === 0 ? -1 : pageIndex}
            pageCount={pageCount}
            pageSize={pageSize}
            onNextPage={nextPage}
            onPreviousPage={previousPage}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </div>
      )}
    </DefaultLayout>
  );
}

export default CompanySubcontractors;
